/**
 * useTodayBoardData — pure transform hook for the Today Board page.
 *
 * Takes the same enrichedData Map that useMapAllocations produces
 * and derives counters, attention items, and schedule sections.
 * Zero additional database queries.
 */

import { useMemo } from 'react'
import type { AllocationEnrichedData, AircraftDetailInfo, StatusRowSeverity } from '@/types/aircraft-card'
import { computeStatusRows, type StatusRowDef } from '@/lib/utils/compute-status-rows'
import type { AircraftSchedule, ScheduleOwnerInfo } from '@/types/aircraft-schedules'
import type { ScheduleConfidence, ScheduleSource, ScheduleStatus } from '@/types/aircraft-schedules'

// ── Types ─────────────────────────────────────────────

export interface TodayBoardCounters {
  arriving: number
  onGround: number
  departing: number
  occupancyPercent: number
}

export interface TodayBoardItem {
  allocationId: string
  data: AllocationEnrichedData
  rows: StatusRowDef[]
  worstSeverity: StatusRowSeverity
  nonOkCount: number
}

export interface ScheduleItem {
  allocationId: string
  data: AllocationEnrichedData
  /** Whether this item also appears in the needs-attention feed */
  isInAttentionFeed: boolean
  /** Whether this item has warning-level or worse status rows */
  hasWarnings: boolean
  /** Whether this item is departing on the selected date */
  isDepartingToday: boolean
  /** Whether this is an arriving or departing movement */
  movementType: 'arriving' | 'departing'
  /** ISO string for chronological sort (check-in time for arrivals, check-out for departures) */
  sortTime: string | null
  /** Whether this item was derived from aircraft_schedules or from presence/contract logic */
  dataOrigin: 'schedule' | 'presence'
  // ── Schedule-sourced fields (nullable — only present when dataOrigin === 'schedule') ──
  scheduleId?: string | null
  scheduledTime?: string | null
  airportIcao?: string | null
  airportName?: string | null
  confidence?: ScheduleConfidence | null
  source?: ScheduleSource | null
  scheduleStatus?: ScheduleStatus | null
  flightReference?: string | null
  /** Integration provider code (e.g. 'simulator', 'flightbridge') */
  providerCode?: string | null
  /** Schedule notes (free text from CSR or integration) */
  notes?: string | null
  // ── FlightBridge reservation detail (from the schedule's owner client join) ──
  /** Owner/operator client + primary contact for this aircraft */
  owner?: ScheduleOwnerInfo | null
  /** Passenger / crew counts for this leg */
  passengerCount?: number | null
  crewCount?: number | null
  /** The paired opposite leg's scheduled time + airport (departure for an arrival item, and vice-versa) */
  pairedScheduledTime?: string | null
  pairedAirportIcao?: string | null
  // ── Demo triage fields (from services_requested.gen — Masters event generator) ──
  /** Injected triage color (green/amber/red); null for non-demo schedules */
  triageColor?: 'green' | 'amber' | 'red' | null
  /** The specific condition that drove an amber/red classification */
  triageCondition?: string | null
  /** Requested services (services_requested.services) for chip display */
  services?: Array<Record<string, unknown>>
}

export interface SeveritySummary {
  critical: number
  warning: number
  notice: number
}

export interface TodayBoardData {
  counters: TodayBoardCounters
  needsAttention: TodayBoardItem[]
  schedule: {
    arriving: ScheduleItem[]
    departing: ScheduleItem[]
    movements: ScheduleItem[]
  }
  severitySummary: SeveritySummary
}

// ── Helpers ───────────────────────────────────────────

const SEVERITY_RANK: Record<StatusRowSeverity, number> = {
  critical: 1,
  warning: 2,
  notice: 3,
  info: 4,
  ok: 5,
}

function isSameDay(dateStr: string, target: Date): boolean {
  const d = new Date(dateStr)
  return (
    d.getFullYear() === target.getFullYear() &&
    d.getMonth() === target.getMonth() &&
    d.getDate() === target.getDate()
  )
}

function isOnOrBeforeDay(dateStr: string, target: Date): boolean {
  const d = new Date(dateStr)
  const t = new Date(target.getFullYear(), target.getMonth(), target.getDate(), 23, 59, 59, 999)
  return d <= t
}

/**
 * Build a minimal AllocationEnrichedData stub from schedule aircraft info.
 * Used for schedule-only items (no allocation exists for this aircraft).
 */
function buildScheduleOnlyStub(sched: AircraftSchedule): AllocationEnrichedData {
  const info = sched.aircraft
  const aircraft: AircraftDetailInfo = {
    id: info?.id ?? sched.aircraftId,
    tailNumber: info?.tailNumber ?? 'Unknown',
    manufacturer: info?.manufacturer ?? null,
    model: info?.model ?? null,
    typeCode: info?.typeCode ?? null,
    category: null,
    length: null,
    wingspan: null,
    height: null,
    tailHeight: null,
    emptyWeight: null,
    maxTakeoffWeight: null,
    registrationCountry: null,
    serialNumber: null,
    yearManufactured: null,
    annualInspectionDue: null,
    insuranceExpiry: null,
    status: 'active',
    notes: null,
    ownerName: info?.owner?.displayName ?? null,
    owner: null,
    operator: null,
    defaultContactId: null,
    defaultContact: null,
    defaultFuelType: null,
  }

  return {
    allocationId: '',
    aircraft,
    allocation: {
      areaId: null,
      areaName: null,
      unitId: null,
      unitCode: null,
      positionType: 'ramp',
      customLabel: null,
      showLabel: null,
      colorOverride: null,
      hasCollision: null,
      collisionDetails: null,
      leaseContractId: null,
      transientStayId: null,
      status: 'reserved',
      presence: sched.direction === 'inbound' ? 'expected' : 'present',
      renderMode: 'footprint',
      startsAt: sched.scheduledAt,
      endsAt: null,
      expectedReturnAt: null,
    },
    leaseContract: null,
    transientStay: null,
    ownerContacts: null,
    operatorContacts: null,
    fuelSummary: null,
  }
}

/**
 * Pull demo triage color + requested services out of a schedule's
 * services_requested jsonb. Shape written by the Masters generator:
 *   { services: [...], gen: { triage, triage_condition, ... } }
 * Defensive: also accepts a bare services array (legacy/other integrations).
 */
function extractScheduleMeta(servicesRequested: unknown): {
  triageColor: 'green' | 'amber' | 'red' | null
  triageCondition: string | null
  services: Array<Record<string, unknown>>
} {
  let triageColor: 'green' | 'amber' | 'red' | null = null
  let triageCondition: string | null = null
  let services: Array<Record<string, unknown>> = []

  if (servicesRequested && typeof servicesRequested === 'object') {
    if (Array.isArray(servicesRequested)) {
      services = servicesRequested as Array<Record<string, unknown>>
    } else {
      const o = servicesRequested as Record<string, unknown>
      const gen = o.gen as Record<string, unknown> | undefined
      if (gen) {
        const t = gen.triage
        if (t === 'green' || t === 'amber' || t === 'red') triageColor = t
        triageCondition = (gen.triage_condition as string) ?? null
      }
      if (Array.isArray(o.services)) services = o.services as Array<Record<string, unknown>>
    }
  }

  return { triageColor, triageCondition, services }
}

/**
 * Resolve the paired opposite leg for a schedule (inbound ↔ outbound, linked by pair_id),
 * then assemble the reservation-detail fields shared by both build passes.
 */
function buildScheduleExtras(
  sched: AircraftSchedule,
  schedulesByAircraft: Map<string, AircraftSchedule[]>,
): Pick<
  ScheduleItem,
  'owner' | 'passengerCount' | 'crewCount' | 'pairedScheduledTime' | 'pairedAirportIcao'
> {
  const siblings = schedulesByAircraft.get(sched.aircraftId) ?? []
  // The outbound leg carries pair_id = inbound.id. So find by either direction.
  const paired = siblings.find(
    (s) => s.id !== sched.id && (s.pairId === sched.id || s.id === sched.pairId),
  )
  return {
    owner: sched.aircraft?.owner ?? null,
    passengerCount: sched.passengerCount,
    crewCount: sched.crewCount,
    pairedScheduledTime: paired?.scheduledAt ?? null,
    pairedAirportIcao: paired?.airportIcao ?? null,
  }
}

// ── Hook ──────────────────────────────────────────────

export function useTodayBoardData(
  enrichedData: Map<string, AllocationEnrichedData>,
  selectedDate: Date,
  totalUnits: number,
  locationSchedules?: AircraftSchedule[],
): TodayBoardData {
  return useMemo(() => {
    const attentionItems: TodayBoardItem[] = []
    const attentionIds = new Set<string>()

    const arriving: ScheduleItem[] = []
    const departing: ScheduleItem[] = []

    let arrivingCount = 0
    let onGroundCount = 0
    let departingCount = 0
    let occupiedUnits = 0

    const severitySummary: SeveritySummary = { critical: 0, warning: 0, notice: 0 }

    // ── Build schedule lookup by aircraftId ──
    const schedulesByAircraft = new Map<string, AircraftSchedule[]>()
    if (locationSchedules) {
      for (const sched of locationSchedules) {
        const existing = schedulesByAircraft.get(sched.aircraftId)
        if (existing) {
          existing.push(sched)
        } else {
          schedulesByAircraft.set(sched.aircraftId, [sched])
        }
      }
    }

    // Track which aircraftId+direction combos we already added via schedule data
    const scheduleCovered = new Set<string>()
    // Track aircraft IDs that have allocations (for schedule-only pass)
    const allocatedAircraftIds = new Set<string>()

    for (const [allocationId, data] of enrichedData) {
      allocatedAircraftIds.add(data.aircraft.id)
      const { presence, status } = data.allocation
      const isAllocTerminal = status === 'completed' || status === 'cancelled'

      // ── Active allocations: categorize, count, compute attention ──

      let hasWarningsOrWorse = false

      // Presence-based flags (always false for terminal allocations)
      const isExpected = !isAllocTerminal && presence === 'expected'
      const isPresent = !isAllocTerminal && (presence === 'present' || presence === 'away')

      const isArrivingOnDate = isExpected && (
        (data.transientStay?.checkIn && isOnOrBeforeDay(data.transientStay.checkIn, selectedDate)) ||
        (data.allocation.startsAt && isOnOrBeforeDay(data.allocation.startsAt, selectedDate))
      )

      const isDepartingOnDate = isPresent && (
        (data.transientStay?.expectedCheckOut &&
          data.transientStay.status === 'checked_in' &&
          !data.transientStay.actualCheckOut &&
          isSameDay(data.transientStay.expectedCheckOut, selectedDate)) ||
        (data.leaseContract?.endDate &&
          data.leaseContract.status === 'active' &&
          isSameDay(data.leaseContract.endDate, selectedDate))
      )

      // Count for counters (skip terminal)
      if (isPresent) {
        onGroundCount++
        if (data.allocation.unitId) occupiedUnits++
      }

      // ── Compute status rows for attention feed (skip terminal) ──

      if (!isAllocTerminal) {
        const rows = computeStatusRows(data)
        const nonOkRows = rows.filter((r) => r.severity !== 'ok')
        hasWarningsOrWorse = nonOkRows.some(
          (r) => r.severity === 'critical' || r.severity === 'warning',
        )

        if (hasWarningsOrWorse) {
          const worstSeverity = nonOkRows.reduce<StatusRowSeverity>(
            (worst, row) =>
              SEVERITY_RANK[row.severity] < SEVERITY_RANK[worst] ? row.severity : worst,
            'ok',
          )

          attentionItems.push({
            allocationId,
            data,
            rows,
            worstSeverity,
            nonOkCount: nonOkRows.length,
          })
          attentionIds.add(allocationId)

          // Count severities
          for (const row of nonOkRows) {
            if (row.severity === 'critical') severitySummary.critical++
            else if (row.severity === 'warning') severitySummary.warning++
            else if (row.severity === 'notice') severitySummary.notice++
          }
        }
      }

      // ── Build schedule items — always (even for terminal allocations) ──

      const allocSchedules = schedulesByAircraft.get(data.aircraft.id)

      if (allocSchedules && allocSchedules.length > 0) {
        // Produce schedule-sourced items
        for (const sched of allocSchedules) {
          const movementType: 'arriving' | 'departing' =
            sched.direction === 'inbound' ? 'arriving' : 'departing'
          const isCompleted = sched.status === 'completed'

          // Count non-completed schedules in counters
          if (!isCompleted) {
            if (movementType === 'arriving') arrivingCount++
            if (movementType === 'departing') departingCount++
          }

          const targetList = movementType === 'arriving' ? arriving : departing
          targetList.push({
            allocationId,
            data,
            isInAttentionFeed: hasWarningsOrWorse,
            hasWarnings: hasWarningsOrWorse,
            isDepartingToday: movementType === 'departing',
            movementType,
            sortTime: sched.scheduledAt,
            dataOrigin: 'schedule',
            scheduleId: sched.id,
            scheduledTime: sched.scheduledAt,
            airportIcao: sched.airportIcao,
            airportName: sched.airportName,
            confidence: sched.confidence,
            source: sched.source,
            scheduleStatus: sched.status,
            flightReference: sched.flightReference,
            providerCode: sched.providerCode,
            notes: sched.notes,
            ...buildScheduleExtras(sched, schedulesByAircraft),
            ...extractScheduleMeta(sched.servicesRequested),
          })
          scheduleCovered.add(`${data.aircraft.id}:${movementType}`)
        }
      }

      // Presence-based fallback (only for active allocations, not covered by schedule data)
      if (isArrivingOnDate && !scheduleCovered.has(`${data.aircraft.id}:arriving`)) {
        arrivingCount++
        const arrivalTime = data.transientStay?.checkIn || data.allocation.startsAt || null
        arriving.push({
          allocationId,
          data,
          isInAttentionFeed: hasWarningsOrWorse,
          hasWarnings: hasWarningsOrWorse,
          isDepartingToday: !!isDepartingOnDate,
          movementType: 'arriving',
          sortTime: arrivalTime,
          dataOrigin: 'presence',
        })
      }
      if (isDepartingOnDate && !scheduleCovered.has(`${data.aircraft.id}:departing`)) {
        departingCount++
        const departureTime = data.transientStay?.expectedCheckOut || data.leaseContract?.endDate || null
        departing.push({
          allocationId,
          data,
          isInAttentionFeed: hasWarningsOrWorse,
          hasWarnings: hasWarningsOrWorse,
          isDepartingToday: true,
          movementType: 'departing',
          sortTime: departureTime,
          dataOrigin: 'presence',
        })
      }
    }

    // ── Second pass: schedule-only aircraft (no allocation) ──
    if (locationSchedules) {
      for (const sched of locationSchedules) {
        // Skip if this aircraft already has an allocation
        if (allocatedAircraftIds.has(sched.aircraftId)) continue
        // Skip cancelled schedules (completed still shown with proper mark)
        if (sched.status === 'cancelled') continue

        const movementType: 'arriving' | 'departing' =
          sched.direction === 'inbound' ? 'arriving' : 'departing'

        // Deduplicate: only one item per aircraft+direction
        const key = `${sched.aircraftId}:${movementType}`
        if (scheduleCovered.has(key)) continue
        scheduleCovered.add(key)

        const stubData = buildScheduleOnlyStub(sched)

        // Count non-completed schedules only
        if (sched.status !== 'completed') {
          if (movementType === 'arriving') arrivingCount++
          if (movementType === 'departing') departingCount++
        }

        const targetList = movementType === 'arriving' ? arriving : departing
        targetList.push({
          allocationId: '',
          data: stubData,
          isInAttentionFeed: false,
          hasWarnings: false,
          isDepartingToday: movementType === 'departing',
          movementType,
          sortTime: sched.scheduledAt,
          dataOrigin: 'schedule',
          scheduleId: sched.id,
          scheduledTime: sched.scheduledAt,
          airportIcao: sched.airportIcao,
          airportName: sched.airportName,
          confidence: sched.confidence,
          source: sched.source,
          scheduleStatus: sched.status,
          flightReference: sched.flightReference,
          providerCode: sched.providerCode,
          notes: sched.notes,
          ...buildScheduleExtras(sched, schedulesByAircraft),
          ...extractScheduleMeta(sched.servicesRequested),
        })
      }
    }

    // Sort attention items: worst severity first, then by non-ok count, then tail number
    attentionItems.sort((a, b) => {
      const sevDiff = SEVERITY_RANK[a.worstSeverity] - SEVERITY_RANK[b.worstSeverity]
      if (sevDiff !== 0) return sevDiff
      const countDiff = b.nonOkCount - a.nonOkCount
      if (countDiff !== 0) return countDiff
      return a.data.aircraft.tailNumber.localeCompare(b.data.aircraft.tailNumber)
    })

    // Sort schedule items chronologically by sortTime, then tail number
    const chronoSort = (a: ScheduleItem, b: ScheduleItem) => {
      if (a.sortTime && b.sortTime) {
        const diff = new Date(a.sortTime).getTime() - new Date(b.sortTime).getTime()
        if (diff !== 0) return diff
      }
      if (a.sortTime && !b.sortTime) return -1
      if (!a.sortTime && b.sortTime) return 1
      return a.data.aircraft.tailNumber.localeCompare(b.data.aircraft.tailNumber)
    }

    arriving.sort(chronoSort)
    departing.sort(chronoSort)

    // Merged movements list — all arrivals + departures sorted chronologically
    const movements = [...arriving, ...departing].sort(chronoSort)

    const occupancyPercent = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0

    return {
      counters: {
        arriving: arrivingCount,
        onGround: onGroundCount,
        departing: departingCount,
        occupancyPercent,
      },
      needsAttention: attentionItems,
      schedule: { arriving, departing, movements },
      severitySummary,
    }
  }, [enrichedData, selectedDate, totalUnits, locationSchedules])
}
